import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createAsyncStorage } from '@/lib/asyncStorage'
import { chromeStorage } from '@/lib/storage'

export interface HabitCompletion {
  relativeDay: number // شماره روز نسبی (1، 2، 3، ...)
  completionDate: string // تاریخ واقعی تکمیل (ISO string)
}

export interface Habit {
  id: string
  title: string
  monthlyGoal: number
  year: number
  month: number // Jalali month
  startDate: string // ISO date string - تاریخ شروع عادت
  endDate: string // ISO date string - تاریخ پایان عادت
  completions: HabitCompletion[] // آرایه تکمیل‌های روزانه
}

export interface HabitState {
  habits: Habit[]
  addHabit: (habit: Omit<Habit, 'id' | 'completions'> & { id?: string }) => void
  updateHabit: (id: string, habit: Partial<Habit>) => void
  deleteHabit: (id: string) => void
  toggleDay: (habitId: string, dateString: string) => void // تاریخ تکمیل (ISO string)
  getHabitByYearMonth: (year: number, month: number) => Habit[]
  clearAllHabits: () => void // clear all habits
}

export const useHabitStore = create<HabitState>()(
  persist(
    (set, get) => ({
      habits: [],
      addHabit: (habitData) =>
        set((state) => {
          const currentHabits = Array.isArray(state.habits) ? state.habits : []

          // parse and validate dates
          let startDate = new Date(habitData.startDate)
          let endDate = new Date(habitData.endDate)
          
          if (isNaN(startDate.getTime())) startDate = new Date()
          if (isNaN(endDate.getTime())) {
            endDate = new Date(startDate)
            endDate.setDate(startDate.getDate() + (habitData.monthlyGoal || 30) - 1)
          }
          if (endDate < startDate) {
            const tmp = startDate
            startDate = endDate
            endDate = tmp
          }

          const habit: Habit = {
            ...habitData,
            id: habitData.id || `habit-${Date.now()}`,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            completions: [],
          }
          return { habits: [...currentHabits, habit] }
        }),
      updateHabit: (id, updates) =>
        set((state) => {
          const currentHabits = Array.isArray(state.habits) ? state.habits : []
          return {
            habits: currentHabits.map((h) => (h.id === id ? { ...h, ...updates } : h)),
          }
        }),
      deleteHabit: (id) =>
        set((state) => {
          const currentHabits = Array.isArray(state.habits) ? state.habits : []
          return {
            habits: currentHabits.filter((h) => h.id !== id),
          }
        }),
      toggleDay: (habitId, dateString) =>
        set((state) => {
          const currentHabits = Array.isArray(state.habits) ? state.habits : []
          return {
            habits: currentHabits.map((h) => {
              if (h.id !== habitId) return h

              // ensure completions is an array
              const completions = Array.isArray(h.completions) ? h.completions : []

              // check if this date is already completed
              const existing = completions.find(
                (c) => c.completionDate.slice(0, 10) === dateString.slice(0, 10)
              )

              if (existing) {
                // remove completion
                return {
                  ...h,
                  completions: completions.filter(
                    (c) => c.completionDate.slice(0, 10) !== dateString.slice(0, 10)
                  ),
                }
              } else {
                // add completion - calculate relative day
                const startDate = new Date(h.startDate)
                const completionDate = new Date(dateString)
                const diffDays = Math.floor(
                  (completionDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
                )
                const relativeDay = diffDays + 1

                return {
                  ...h,
                  completions: [
                    ...completions,
                    {
                      relativeDay,
                      completionDate: completionDate.toISOString(),
                    },
                  ].sort((a, b) => a.relativeDay - b.relativeDay),
                }
              }
            }),
          }
        }),
      getHabitByYearMonth: (year, month) => {
        const habits = get().habits
        const currentHabits = Array.isArray(habits) ? habits : []
        return currentHabits.filter((h) => h.year === year && h.month === month)
      },
      clearAllHabits: () => {
        set({ habits: [] })
      },
    }),
    {
      name: 'habits',
      storage: createAsyncStorage(),
      onRehydrateStorage: () => (state) => {
        if (!state || !Array.isArray(state.habits)) {
          state = state || {} as any
          state.habits = []
          return
        }

        // Filter: keep only valid habits (those with proper startDate and endDate)
        state.habits = state.habits.filter((h: any) => {
          const hasValidStartDate = h.startDate && !isNaN(new Date(h.startDate).getTime())
          const hasValidEndDate = h.endDate && !isNaN(new Date(h.endDate).getTime())
          const hasCompletions = Array.isArray(h.completions)
          
          return hasValidStartDate && hasValidEndDate && hasCompletions
        }).map((h: any) => {
          // Ensure completions is array
          return {
            ...h,
            completions: Array.isArray(h.completions) ? h.completions : []
          }
        }) as Habit[]
      },
    }
  )
)

export const initializeHabits = async (): Promise<void> => {
  try {
    // Rehydrate from persisted storage
    await useHabitStore.persist.rehydrate()
    
    // Check if data was loaded
    const state = useHabitStore.getState()
    if (state.habits && Array.isArray(state.habits)) {
      console.log('✅ Habits rehydrated:', state.habits.length, 'habits found')
    } else {
      console.log('✅ Habits rehydrated (empty)')
    }
  } catch (error) {
    console.error('❌ Failed to initialize habits:', error)
  }
}
